import React, { useEffect, useMemo, useState } from 'react';
import {
  Btn, Chip, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, SubTabs, panelPost, useAdminGet,
} from '../../kit';
import { Modal } from '../emailKit';
import {
  BulkTagBar, EMPTY_TAG_FILTER, PERSONAS, PERSONA_LABEL, RowCheckbox, TagFilter, type TagFilterValue,
  describeTagFilter, invalidateTagOptions, tagCell, tagFilterParams, tagText, useRowSelection, useTagOptions,
} from '../../tags/shared';
import ImportHistory from './ImportHistory';
import ImportWizard from './ImportWizard';
import Timeline from './Timeline';
import { Ago, PEOPLE, SourceRef, TimelineTarget, emailPeople, fmtDay, num } from './shared';

/**
 * People — the Email Center's directory of everyone we can e-mail
 * (backend: Backend/admin_people.py, section "people").
 *
 *  · Directory       every account with the standard filters (member state,
 *                    custom and automatic tags, persona, profile %, the date
 *                    facts), how each person reached us, and how many of our
 *                    e-mails they got, opened and clicked. Tick people to tag
 *                    them, set their persona, or hand them to the campaign
 *                    composer ("E-mail these" → the utech:email-people event).
 *  · Import          a CSV / XLSX with the columns chosen by the admin.
 *  · Import history  every import and what happened to each of its rows.
 *
 * Any row, and the "timeline for an e-mail" box, opens the person's timeline:
 * what we sent, what happened to it, visits and product activity.
 */

const TABS = ['Directory', 'Import', 'Import history'];

const People: React.FC = () => {
  const [tab, setTab] = useState(TABS[0]);
  const [source, setSource] = useState<SourceRef | null>(null);
  const [timeline, setTimeline] = useState<TimelineTarget | null>(null);
  const viewSource = (s: SourceRef) => { setSource(s); setTab('Directory'); };
  return (
    <div className="flex flex-col gap-4">
      <SubTabs tabs={TABS} active={tab} onChange={setTab} />
      {tab === 'Directory' && <Directory source={source} onClearSource={() => setSource(null)} onOpen={setTimeline} />}
      {tab === 'Import' && <ImportWizard onViewPeople={viewSource} />}
      {tab === 'Import history' && <ImportHistory onViewPeople={viewSource} onOpen={setTimeline} />}
      {timeline && <Timeline {...timeline} onClose={() => setTimeline(null)} />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Directory
// ---------------------------------------------------------------------------
function useDebounced(value: string, ms = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** One click narrows to exactly that value; clicking it again clears it. */
const only = (cur: string[], k: string) => (cur.length === 1 && cur[0] === k ? [] : [k]);
const isOnly = (cur: string[], k: string) => cur.length === 1 && cur[0] === k;

const KpiGroup: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="flex flex-col gap-1.5">
    <div className="text-[11px] uppercase tracking-wide text-grayscale-1100 font-bold">{title}</div>
    <KPIRow>{children}</KPIRow>
  </div>
);

const COLUMNS = ['email', 'name', 'company', 'state', 'persona_label', 'created_at', 'added_via', 'last_activity_at',
  'last_emailed_at', 'emails_sent', 'opens', 'clicks', 'profile_pct'];
const LABELS: Record<string, string> = {
  email: 'Email', name: 'Name', company: 'Company', state: 'State / tags', persona_label: 'Persona',
  created_at: 'First added', added_via: 'Added via', last_activity_at: 'Last activity',
  last_emailed_at: 'Last e-mailed', emails_sent: 'Sent', opens: 'Opened', clicks: 'Clicked', profile_pct: 'Profile %',
};

const Directory: React.FC<{
  source: SourceRef | null; onClearSource: () => void; onOpen: (t: TimelineTarget) => void;
}> = ({ source, onClearSource, onOpen }) => {
  const opts = useTagOptions();
  const [tf, setTf] = useState<TagFilterValue>(EMPTY_TAG_FILTER);
  const [qRaw, setQRaw] = useState('');
  const q = useDebounced(qRaw);
  const [excludeTeam, setExcludeTeam] = useState(true);
  const [stamp, setStamp] = useState(0);
  const [lookup, setLookup] = useState('');
  const [lookupErr, setLookupErr] = useState('');
  const [persona, setPersona] = useState('');
  const [personaConfirm, setPersonaConfirm] = useState(false);
  const [note, setNote] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const params = useMemo(() => {
    const p: Record<string, string> = { ...tagFilterParams(tf), exclude_team: excludeTeam ? '1' : '0' };
    if (q.trim()) p.q = q.trim();
    if (source) p.source = String(source.id);
    return p;
  }, [tf, q, excludeTeam, source]);
  const filterKey = JSON.stringify(params);

  const overview = useAdminGet<any>(PEOPLE, 'overview', { exclude_team: excludeTeam ? '1' : '0' },
    [excludeTeam, stamp]);
  const list = useAdminGet<any>(PEOPLE, 'list', params, [filterKey, stamp]);
  const sel = useRowSelection({ idKey: 'user_id', resetKey: filterKey });
  const reload = () => setStamp(s => s + 1);

  // `state` and `persona_label` as text are what the CSV export carries; the
  // cells render pills.
  const rows: any[] = useMemo(() => (list.data?.rows || []).map((r: any) => ({
    ...r,
    state: [r.lifecycle_label, tagText(r)].filter(Boolean).join(', '),
    persona_label: r.persona_label || '',
  })), [list.data]);
  const total = Number(list.data?.total ?? 0);
  const ov = overview.data;

  const summary = useMemo(() => {
    const out = describeTagFilter(tf, k => opts.tagByKey[k]?.label || k);
    if (q.trim()) out.push(`E-mail / name / company contains “${q.trim()}”`);
    if (source) out.push(`From the import “${source.label}”`);
    out.push(excludeTeam ? 'Team left out' : 'Team included');
    return out;
  }, [tf, q, source, excludeTeam, opts.tagByKey]);

  const openLookup = () => {
    const e = lookup.trim().toLowerCase();
    if (!EMAIL_RE.test(e)) { setLookupErr('Type a full e-mail address'); return; }
    setLookupErr('');
    onOpen({ email: e });
  };

  const applyPersona = async () => {
    setPersonaConfirm(false); setNote(null);
    const { data, error } = await panelPost(PEOPLE, 'set_persona', { user_ids: sel.ids, persona });
    if (error || !data) { setNote({ kind: 'error', text: error || 'Could not set the persona' }); return; }
    setNote({ kind: 'ok', text: `${persona ? `Persona “${PERSONA_LABEL[persona]}” set` : 'Persona cleared'} on `
      + `${num(data.users)} account(s).` });
    invalidateTagOptions();
    reload();
  };

  return (
    <div className="flex flex-col gap-4">
      <Section title="Everyone we can e-mail"
        right={<div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs text-grayscale-1100 flex items-center gap-1.5 cursor-pointer"
            title="@universitytech.io addresses, the analytics exclusion list and billing admins">
            <input type="checkbox" checked={excludeTeam} onChange={e => setExcludeTeam(e.target.checked)} />
            Leave our team out
          </label>
          <Btn small onClick={reload}>Refresh</Btn>
        </div>}>
        {overview.error && <Msg kind="error">{overview.error}</Msg>}
        {overview.loading && !ov ? <Loading /> : ov && (
          <>
            <div className="text-xs text-grayscale-1100 -mt-1">
              {num(ov.totals?.people)} people · {num(ov.totals?.signed_in)} have signed in ·
              {' '}{num(ov.totals?.with_persona)} with a persona · {num(ov.totals?.imported)} came in an import
              {ov.team_excluded ? ` · ${num(ov.team_excluded)} team accounts left out` : ''} · click a number to filter by it
            </div>
            <KpiGroup title="Member state">
              {(ov.lifecycle || []).map((l: any) => (
                <KPI key={l.key} label={l.label} value={num(l.n)} active={isOnly(tf.lifecycle, l.key)}
                  onClick={() => setTf(f => ({ ...f, lifecycle: only(f.lifecycle, l.key) }))} />
              ))}
            </KpiGroup>
            <KpiGroup title="Automatic tags">
              {(ov.auto_tags || []).map((t: any) => (
                <KPI key={t.key} label={t.label} value={num(t.n)}
                  active={isOnly(tf.tags, t.key) && tf.tagMode === 'any'}
                  onClick={() => setTf(f => ({ ...f, tags: only(f.tags, t.key), tagMode: 'any' }))} />
              ))}
            </KpiGroup>
            <KpiGroup title="Persona">
              {(ov.personas || []).map((p: any) => (
                <KPI key={p.key} label={p.label} value={num(p.n)} active={isOnly(tf.persona, p.key)}
                  onClick={() => setTf(f => ({ ...f, persona: only(f.persona, p.key) }))} />
              ))}
            </KpiGroup>
          </>
        )}
      </Section>

      <Section title="Find people" right={<Btn small onClick={() => { setTf(EMPTY_TAG_FILTER); setQRaw(''); onClearSource(); }}>
        Reset filters</Btn>}>
        <div className="flex items-end gap-3 flex-wrap">
          <label className="flex flex-col gap-1 text-xs text-grayscale-1100 flex-1 min-w-[16rem]">
            Search everyone
            <input value={qRaw} onChange={e => setQRaw(e.target.value)} placeholder="E-mail, name or company…"
              className="h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300 w-full" />
          </label>
          <label className="flex flex-col gap-1 text-xs text-grayscale-1100 min-w-[16rem]">
            Timeline for any e-mail address (account or not)
            <span className="flex gap-2">
              <input value={lookup} onChange={e => setLookup(e.target.value)} placeholder="someone@example.org"
                onKeyDown={e => { if (e.key === 'Enter') openLookup(); }}
                className="h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300 flex-1" />
              <Btn onClick={openLookup} disabled={!lookup.trim()}>Open</Btn>
            </span>
            {lookupErr && <span className="text-red-600">{lookupErr}</span>}
          </label>
        </div>
        {source && (
          <div><Chip label="Import" value={`#${source.id} ${source.label}`} onClear={onClearSource} /></div>
        )}
        <TagFilter value={tf} onChange={v => setTf(v)} />
      </Section>

      <Section title={`Selected (${num(sel.count)})`}>
        <BulkTagBar title="Tags" userIds={sel.ids} onDone={reload} onClear={sel.count ? sel.clear : undefined} />
        <div className="flex items-end gap-3 flex-wrap">
          <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
            Primary persona
            <Select value={persona} onChange={setPersona}
              options={['', ...PERSONAS.filter(p => p.key !== 'none').map(p => p.key)]}
              labels={{ '': 'Clear the persona', ...PERSONA_LABEL }} />
          </label>
          <Btn disabled={!sel.count} onClick={() => setPersonaConfirm(true)}>Set persona</Btn>
          <Btn kind="primary" disabled={!sel.count} onClick={() => emailPeople(sel.ids)}
            title="Opens the campaign composer with these people as the audience">
            E-mail these ({num(sel.count)})
          </Btn>
        </div>
        {note && <Msg kind={note.kind}>{note.text}</Msg>}
      </Section>

      <Section title={`People (${num(total)})`}
        right={<div className="flex items-center gap-2 flex-wrap">
          <Btn small disabled={!rows.length} onClick={() => sel.togglePage(rows)}>
            {sel.allOnPage(rows) ? 'Unselect all' : `Select all ${num(rows.length)}`}
          </Btn>
          {sel.count > 0 && <Btn small onClick={sel.clear}>Clear ({num(sel.count)})</Btn>}
          <Btn small disabled={!rows.length} onClick={() => emailPeople(rows.map(r => r.user_id))}
            title="Opens the campaign composer with everyone listed">
            E-mail everyone listed
          </Btn>
        </div>}>
        <div className="text-xs text-grayscale-1100 -mt-1">{summary.join(' · ')}</div>
        {total > rows.length && (
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Showing the first {num(rows.length)} of {num(total)} — narrow the filters to see the rest.
          </div>
        )}
        {list.error && <Msg kind="error">{list.error}</Msg>}
        {list.loading && !list.data ? <Loading /> : (
          <div className={list.loading ? 'opacity-60' : ''}>
            <DataTable rows={rows} columns={COLUMNS} labels={LABELS} csvName="people" maxHeight="40rem"
              empty="Nobody matches these filters."
              render={{
                email: r => (
                  <span className="inline-flex items-center gap-2">
                    <RowCheckbox sel={sel} row={r} />
                    <button type="button" onClick={() => onOpen({ userId: r.user_id })} title="Open the timeline"
                      className="text-left hover:underline underline-offset-2">{r.email}</button>
                  </span>
                ),
                name: r => (
                  <button type="button" onClick={() => onOpen({ userId: r.user_id })} title="Open the timeline"
                    className="text-left hover:underline underline-offset-2">{r.name || '—'}</button>
                ),
                state: tagCell,
                created_at: r => <span title={r.created_at}>{fmtDay(r.created_at)}</span>,
                last_activity_at: r => <Ago value={r.last_activity_at} empty="never" />,
                last_emailed_at: r => <Ago value={r.last_emailed_at} empty="never" />,
                profile_pct: r => `${num(r.profile_pct)}%`,
              }} />
          </div>
        )}
      </Section>

      {personaConfirm && (
        <Modal title={persona ? `Set “${PERSONA_LABEL[persona]}” on ${num(sel.count)} account(s)?`
          : `Clear the persona of ${num(sel.count)} account(s)?`}
          onClose={() => setPersonaConfirm(false)}
          footer={<>
            <Btn onClick={() => setPersonaConfirm(false)}>Cancel</Btn>
            <Btn kind="primary" onClick={applyPersona}>{persona ? 'Set persona' : 'Clear persona'}</Btn>
          </>}>
          <p>
            Replaces whatever primary persona these accounts have now — the “What represents you best?” answer
            on their profile.
          </p>
        </Modal>
      )}
    </div>
  );
};

export default People;
